import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  DatabaseImportResult,
  DatabaseRowsResponse,
  DatabaseTableListResponse,
} from './database-browser.types';

/**
 * Admin database browser API.
 *
 * Backed by `GET /api/admin/database/...` (see backend `DatabaseTable*View`).
 * Every endpoint is system-admin only and only ever exposes the app's own
 * Django models. `exportDatabase` / `importDatabase` move the whole dataset
 * between environments as a zip archive (`database_transfer.py`).
 */
@Injectable({ providedIn: 'root' })
export class DatabaseBrowserService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/api/admin/database`;

  getTables(): Observable<DatabaseTableListResponse> {
    return this.http.get<DatabaseTableListResponse>(`${this.base}/tables/`);
  }

  getRows(
    tableKey: string,
    options: { page: number; pageSize: number; search?: string; orderBy?: string },
  ): Observable<DatabaseRowsResponse> {
    let params = new HttpParams()
      .set('page', String(options.page))
      .set('page_size', String(options.pageSize));
    if (options.search) {
      params = params.set('search', options.search);
    }
    if (options.orderBy) {
      params = params.set('order_by', options.orderBy);
    }
    return this.http.get<DatabaseRowsResponse>(
      `${this.base}/tables/${encodeURIComponent(tableKey)}/rows/`,
      { params },
    );
  }

  /** Download the whole database as a zip. The full response is returned so the
   *  caller can read the server-chosen filename from `Content-Disposition`. */
  exportDatabase(): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.base}/export/`, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  /** Replace this environment's data with an export archive. A refused
   *  environment arrives as a 200 with `status: 'error'`. */
  importDatabase(file: File): Observable<DatabaseImportResult> {
    const body = new FormData();
    body.append('file', file, file.name);
    return this.http.post<DatabaseImportResult>(`${this.base}/import/`, body);
  }
}
